import {
  IAboutBrand,
  IDiscount,
  IOpticalbenefits,
  IOurGuarantees,
  IProductInfo,
  IPromotions,
  IRebates,
  IStoreAvailability,
} from '../common/interfaces/IContentModel';
import { IImages, IIngredientsList, IMedicineImages } from '../common/interfaces/IOTCMedicineModel';
import { IActionTray } from '../components/complex/actionTray/IActionTray';
import { ICarousel } from '../components/complex/carousel/ICarousel';
import { IMarketingBlock } from '../components/complex/marketingBlock/IMarketingBlock';
import {
  IActionList,
  IBanner,
  ICard,
  IMarketingGridItem,
  MARKETINGCONTENTTYPE,
} from '../components/complex/marketingGrid/IMarketingGrid';
import { ITab } from '../components/complex/tab/ITab';
import {
  IAmountSubgroup,
  IBenefitTablesContent,
} from '../container/account/benefits/components/interfaces/ICommon';
import { ISavingsPlan } from '../container/savings-plan/ISavingsPlan';
import { ISiteMap, SiteMap } from '../services/content';
import { ISiteLogo } from '../types';
import { checkDateValidity } from './dateUtils';
import { safeGet } from './objectUtils';

export function unwrapActionTray(items: any, SITE_MAP: ISiteMap): Array<IActionTray> {
  if (!items) return [];
  return items?.map((actionTray: any): IActionTray => {
    const action = actionTray.actions;
    const [title, url] = action?.is_external_link
      ? [action?.enter_external_url?.title, action?.enter_external_url?.href]
      : [action?.select_internal_url?.title, action?.select_internal_url?.page_name];
    const [src, alt] = [actionTray?.media.url, actionTray?.alt];

    // find the reference in site map link
    const siteMapLinkUid = action?.select_internal_url?.page?.[0]?.uid ?? undefined;
    let siteMapLink = undefined;
    if (siteMapLinkUid !== undefined) {
      siteMapLink =
        siteMapLinkUid && SITE_MAP[SiteMap.SITE_MAP_LINK]
          ? SITE_MAP[SiteMap.SITE_MAP_LINK][siteMapLinkUid]
          : undefined;
    }

    return {
      action: {
        title: title,
        url: siteMapLink ? siteMapLink.url : url,
        isExternal: siteMapLink ? siteMapLink.isExternal : action?.is_external_link,
      },
      media: { src: src, alt: alt },
    };
  });
}

export function unwrapCarousel(carousel: any, SITE_MAP: ISiteMap): ICarousel {
  const carouselItems: ICarousel = {
    autoInterval: 0,
    banner: [],
  };
  if (carousel) {
    carouselItems.autoInterval = carousel?.[0]?.auto_slide_in_seconds_;
    const bannerItems = carousel?.[0]?.items?.filter(
      (item: any) => item?._content_type_uid === 'banner',
    );

    if (bannerItems?.length) {
      carouselItems.banner = unwrapBanner(bannerItems, SITE_MAP);
    }
  }
  return carouselItems;
}

export function unwrapMarketingTiles(
  marketTilesGrid: Array<any>,
  SITE_MAP: ISiteMap,
): Array<ICard> {
  const marketingTiles = [] as any;
  marketTilesGrid?.[0]?.marketing_tile.map((card: any) => {
    marketingTiles.push(unwrapCard(card, SITE_MAP));
  });
  return marketingTiles;
}

export function unwrapMarketingTilesSpacing(marketTilesGrid: Array<any>): number {
  if (!marketTilesGrid || marketTilesGrid.length === 0) {
    return -1;
  }
  return marketTilesGrid[0]?.marketing_tile_spacing ?? -1;
}

export function unwrapBanner(
  banners: any,
  SITE_MAP: ISiteMap,
  position: number = -1,
): Array<IBanner> {
  return banners?.map((banner: any) => {
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const bannerItem: IBanner = {
      alt: '',
      card: undefined,
      banner_style: '',
      layout: 'left-facing',
      src: '',
    };

    bannerItem.layout =
      banner?.background?.alignment?.alignment === 'left'
        ? 'left-facing'
        : banner?.background?.alignment?.alignment === 'right'
          ? 'right-facing'
          : 'center-facing';

    bannerItem.src = isMobile
      ? banner._content_type_uid === 'tile'
        ? banner?.background?.mobile_image?.url
        : banner?.background?.mobile_file?.url
      : banner?.background?.desktop_image?.url;

    bannerItem.alt = banner?.background?.alternate_text;
    bannerItem.banner_style = banner?.banner_style;
    // To avoid the conflict with the position where it is not defined
    if (position !== -1) bannerItem.position = position;

    if (banner?.card) {
      bannerItem.card = unwrapCard(banner?.card, SITE_MAP);
    }
    return bannerItem;
  });
}

export function unwrapCard(card: any, SITE_MAP: ISiteMap): ICard {
  const cardItems: ICard = {
    theme: '',
    header: '',
    description: '',
    tileFontColor: '',
    tileLayout: 'left-facing',
    navigation: [],
    icon: '',
    event: '',
    navigationPanel: [],
  };
  if (card) {
    cardItems.tileLayout =
      card?.alignment === 'left'
        ? 'left-facing'
        : card?.alignment === 'right'
          ? 'right-facing'
          : 'center-facing';
    cardItems.theme = card.card_style;
    cardItems.header = card?.heading?.children[0]?.children[0]?.text;
    cardItems.description = card?.sub_text;
    cardItems.phone = card?.phone;
    if (card.navigation) {
      cardItems.navigation = unwrapActionList(card.navigation, SITE_MAP);
    }
    cardItems.icon = card?.icon;
    cardItems.event = card?.event_rules;
    if (card.navigation_panel && card.navigation_panel.length > 0) {
      cardItems.navigationPanel = unwrapNavigationPanel(
        card?.navigation_panel[0]?.Panel?.modular_blocks3,
        SITE_MAP,
      );
    }
  }
  return cardItems;
}

export function unwrapNavigationPanel(actions: any, SITE_MAP: ISiteMap): ICard {
  if (actions) {
    actions?.map((moduleItem: any) => {
      if (moduleItem?.add_navigation_button) {
        moduleItem['add_button'] = {
          title: moduleItem?.add_navigation_button.button_title,
          url: SITE_MAP[SiteMap.SITE_MAP_LINK][moduleItem?.add_navigation_button.add_button[0]?.uid]
            ?.url,
          isExternal:
            SITE_MAP[SiteMap.SITE_MAP_LINK][moduleItem?.add_navigation_button.add_button[0]?.uid]
              ?.isExternal,
        };
      }
    });
  }
  return actions;
}
export function unwrapTiles(tiles: any, SITE_MAP: ISiteMap): ICard {
  if (tiles) {
    tiles?.map((item: any) => {
      if (item?.actions) {
        item.action = SITE_MAP[SiteMap.SITE_MAP_LINK][item?.actions.action?.[0]?.uid];
      }
    });
  }
  return tiles;
}

//button action list
export function unwrapActionList(actions: any, SITE_MAP: ISiteMap): Array<IActionList> {
  const actionItems: Array<IActionList> = [];
  if (actions) {
    actions?.map((action: any) => {
      let title = '',
        url = '',
        isExternal = true;
      if (action.hasOwnProperty('internal')) {
        const LINK = SITE_MAP[SiteMap.SITE_MAP_LINK][action.internal.navigation_link[0]?.uid];
        title = action.internal.title;
        url = LINK?.url;
        isExternal = LINK?.isExternal;
      }
      if (action.hasOwnProperty('file_download')) {
        title = action.file_download.title;
        url = action.file_download.file.url;
      }
      if (action.hasOwnProperty('add_external_site_button')) {
        title = action.add_external_site_button.enter_url.title;
        url = action.add_external_site_button.enter_url.href;
      }
      actionItems.push({ title: title, url: url, isExternal: isExternal });
    });
  }
  return actionItems;
}

export function unwrapMarketingGrid(marketingGrid: any, SITE_MAP: ISiteMap): IMarketingGridItem[] {
  const marketinContent = marketingGrid?.select_marketing_content_products;
  const marketingItems: IMarketingGridItem[] = marketinContent?.map((item: any, index: number) => {
    const type = Object.keys(item)?.[0];
    const marketingItem: IMarketingGridItem = {
      position: index,
      type: type as MARKETINGCONTENTTYPE,
      value: [],
    };
    switch (type) {
      case MARKETINGCONTENTTYPE.BANNERS:
        marketingItem.value = unwrapBanner(item[type]?.reference, SITE_MAP, index);
        break;
      case MARKETINGCONTENTTYPE.TILES:
        marketingItem.value = unwrapBanner(item[type]?.reference, SITE_MAP, index);
        break;
      case MARKETINGCONTENTTYPE.CAROUSEL:
        // TODO: implement carousel
        marketingItem.value = [item[type]];
        break;
      default:
        marketingItem.value = [];
        break;
    }
    return marketingItem;
  });
  return marketingItems;
}

export function unwrapTab(tab: any, SITE_MAP: ISiteMap): Array<ITab> | null {
  if (!tab || !tab.length) {
    return null;
  }
  return tab[0]?.menu_items.map((item: any) => {
    return {
      canvas: item?.canvas,
      icon: item?.icon,
      link: {
        path: SITE_MAP[SiteMap.SITE_MAP_LINK][item?.internal_link?.internal_site_url[0]?.uid]?.url,
        title: item?.internal_link?.title,
      },
    };
  });
}

export function unwrapBenefitsMarketingBlock(block: any): IMarketingBlock {
  return {
    heading: block[0]?.heading,
    subheading: block[0]?.subheading,
    description: block[0]?.description,
    link: { path: block[0]?.file?.url, title: block[0]?.file?.title },
  };
}

export function unwrapBenefitAmount(input: IAmountSubgroup[]): Record<string, IAmountSubgroup> {
  const result: Record<string, IAmountSubgroup> = {};

  input.forEach(item => {
    item.subgroup.forEach((subgroupItem: any) => {
      result[subgroupItem] = item;
    });
  });

  return result;
}

export function unwrapBenefitTables(benefitTables: any): IBenefitTablesContent {
  const coverageText = {
    structured: benefitTables?.[0]?.structured?.coverage,
    flat: benefitTables?.[0]?.flat?.coverage,
    discount: benefitTables?.[0]?.discount?.coverage,
  };

  const frequencyText = {
    discount: benefitTables?.[0]?.discount?.frequency,
  };

  const footerConditions = {
    inStoreOnly: {
      description: benefitTables?.[0]?.in_store_only?.description,
      conditionText: benefitTables?.[0]?.in_store_only?.condition_text,
    },
    structured: benefitTables?.[0]?.structured?.conditions,
    flat: benefitTables?.[0]?.flat?.conditions,
    discount: benefitTables?.[0]?.discount?.conditions,
    conditionTextSubgroups: benefitTables?.[0]?.condition_text_subgroups ?? [],
  };

  return {
    amountText: unwrapBenefitAmount(benefitTables?.[0]?.amount_text_subgroups ?? []),
    frequencyText: frequencyText,
    coverageText: coverageText,
    footerConditions: footerConditions,
  };
}
export const unwrapProductInfo = (product_info: any): IProductInfo | null => {
  if (!product_info) {
    return null;
  }
  const productInfo = {
    header: '',
    type: '',
    quantity: '',
    manfacturer: '',
    wearSchedule: '',
  };
  productInfo.header = product_info.product_details_title;
  productInfo.type = product_info.attribute_labels.type_label;
  productInfo.quantity = product_info.attribute_labels.quantity_label;
  productInfo.manfacturer = product_info.attribute_labels.manufacturer;
  productInfo.wearSchedule = product_info.attribute_labels.wear_schedule;
  return productInfo;
};
export const upwrapOurGuarantees = (content: any, SITE_MAP: ISiteMap): IOurGuarantees[] => {
  const ourGuaranteesContent = safeGet(content, 'our_guarantees', []);
  const socialMediaIcons = safeGet(ourGuaranteesContent, 'label_icon', []);
  return socialMediaIcons.map((media: any) => {
    let page = safeGet(media, 'link.page', []);
    return {
      media: {
        src: safeGet(media, 'media.url', SITE_MAP.SITE_MAP_LINK[page[0]?.uid]?.url),
        alt: safeGet(media, 'link.title', ''),
      },
      label: media.link.title,
    };
  });
};

export const upwrapStoreAvailability = (content: any): IStoreAvailability => {
  const storeAvailability = safeGet(content, 'store_availability', [])[0];
  return {
    iconCode: safeGet(storeAvailability, 'location_marker.iconcode', 'marker'),
    label: safeGet(storeAvailability, 'try_in_person_title', 'Try it on In person'),
    title: safeGet(storeAvailability, 'check_availability_title', 'Check store availability'),
    btnSearchText: safeGet(storeAvailability, 'location_text_box.search_btn_text', 'Search'),
    btnSearchIconCode: safeGet(
      storeAvailability,
      'location_text_box.search_btn.iconcode',
      'search',
    ),
    placeHolderText: safeGet(
      storeAvailability,
      'location_text_box.place_holder_text',
      'Zip Code or City, State',
    ),
  };
};

export const upwrapOpticalBenefits = (content: any, SITE_MAP: ISiteMap): IOpticalbenefits => {
  const opticalBenefits = safeGet(content, 'product_features.optical_benefits', [])[0];
  return {
    title: safeGet(opticalBenefits, 'use_benefits.benefits_text', 'Use Optical Benefits'),
    btnLogin: {
      title: safeGet(opticalBenefits, 'use_benefits.login.title', 'Login now'),
      url: safeGet(opticalBenefits, 'use_benefits.login.page_name', '/'),
    },
    btnMyBenefits: {
      title: safeGet(opticalBenefits, 'use_benefits.my_benefit.title', 'My benefit'),
      url: safeGet(opticalBenefits, 'use_benefits.my_benefit.page_name', '/'),
    },
    opticalBenefits: safeGet(opticalBenefits, 'user_optical_benefits', ''),
  };
};

export const unwrapAboutBrand = (content: any): IAboutBrand[] => {
  const aboutBrand = safeGet(content, 'about_brand', []);
  // TODO: If at all required unwrap carouselData
  return aboutBrand.map((brand: any) => {
    return {
      layout: safeGet(brand, 'layout', 'image'),
      label: safeGet(brand, 'label', 'About the brand'),
      code: safeGet(brand, 'brand_code', 'Brand'),
      title: safeGet(brand, 'brand_title', 'Brand title'),
      description: safeGet(brand, 'brand_description', 'Brand description'),
      brandImage: {
        alt: safeGet(brand, 'brand_image.title', 'Brand title'),
        src: safeGet(
          brand,
          'brand_image.url',
          'https://images.contentstack.io/v3/assets/bltd38de548fe5a8d52/blt4d0b84d1052e437b/62b1dc69482ba357e65de254/banner1.png',
        ),
      },
      carouselData: safeGet(brand, '', []),
      actionLinks: [
        {
          title: safeGet(brand?.call_to_action[0], 'title', 'Learn more'),
          url: safeGet(brand?.call_to_action[0], 'page_name', ''),
        },
      ],
    };
  });
};

export const unwrapRebates = (content: any, SITE_MAP?: ISiteMap): IRebates[] | null => {
  if (!content || !content[0] || content[0].length === 0) return null;
  const rebates: IRebates[] = [];
  for (let i = 0; i < content[0].length; i++) {
    const rebate_data: IRebates = {
      manufacturerCode: '',
      message: {},
      region: '',
      productCategory: '',
      startingDate: '',
      closingDate: '',
    };
    const startDate = content[0][i].date_range.start_date;
    const endDate = content[0][i].date_range.end_date;
    if (checkDateValidity(startDate, endDate)) continue;
    rebate_data.startingDate = startDate;
    rebate_data.closingDate = endDate;
    rebate_data.manufacturerCode = content[0][i]?.manufacturer_code;
    rebate_data.message = unwrapCard(content[0][i]?.card, SITE_MAP as ISiteMap);
    rebate_data.productCategory = content[0][i]?.product_category?.category;
    rebates.push(rebate_data);
  }
  return rebates;
};

export const unwrapDiscount = (content: any): IDiscount | null => {
  let discount = safeGet(content, 'product_discount', {});
  if (!discount) return null;
  const discounts: IDiscount = {
    header: '',
    description: '',
    region: '',
    productCategory: '',
    startingDate: '',
    closingDate: '',
  };
  discounts.header = discount.heading;
  discounts.description = discount.offer_message;
  discounts.productCategory = discount.product_type?.category;
  return discounts;
};
export const unwrapPromotions = (content: any, SITE_MAP: ISiteMap): IPromotions[] | null => {
  if (!content || content.length < 0) return null;
  const promotions: IPromotions[] = [];
  for (let i = 0; i < content[0].length; i++) {
    const promotions_data = {
      message: {},
      productCategory: '',
      region: '',
      startingDate: '',
      closingDate: '',
    };
    promotions_data.message = unwrapCard(content[0][i].marketing_card, SITE_MAP);
    promotions_data.region = content[0][i].region.code;
    promotions_data.productCategory = content[0][i].product_category.category;
    promotions.push(promotions_data);
  }
  return promotions;
};

export function unwrapSavingsPlan(savingsPlan: any): Array<ISavingsPlan> {
  const planContent: ISavingsPlan[] = [];
  savingsPlan?.map((plan: any) => {
    const savingPlan: ISavingsPlan = {
      title: '',
      span: '',
      price: '',
      planCode: '',
      benefits: [],
      media: {
        src: '',
        alt: '',
      },
      termsLink: {
        title: '',
        url: '',
      },
    };
    savingPlan.title = plan?.header;
    savingPlan.span = plan?.plan_period;
    savingPlan.price = plan?.price;
    savingPlan.planCode = plan?.plan_code;
    savingPlan.media.src = plan?.media?.url;
    savingPlan.media.alt = plan?.media?.title;
    savingPlan.termsLink.title = plan?.pdf_link?.title;
    savingPlan.termsLink.url = plan?.pdf_link?.href;
    plan?.plan_benefits?.map((plan_benefit: any) => {
      savingPlan.benefits.push(plan_benefit?.benefit);
    });
    planContent.push(savingPlan);
  });

  return planContent;
}

export function unwrapAlertMessage(content: any): string {
  return content?.benefit_alert_message;
}

export function unwrapSiteLogo(logo: any): ISiteLogo {
  return { url: logo?.url };
}

export function unwrapContactUs(content: any, SITE_MAP: ISiteMap) {
  const contactUsDetails = safeGet(content, 'contactus_form_details', undefined);
  if (!contactUsDetails?.subjects) {
    return;
  }
  const subjectData = [];
  for (let item = 0; item < contactUsDetails.subjects.length; item++) {
    subjectData.push({
      label: contactUsDetails?.subjects[item].subject,
      value:
        contactUsDetails?.subjects[item].email_group.group +
        ':' +
        contactUsDetails?.subjects[item].subject,
    });
  }
  const aboutFormMessage = contactUsDetails.about_form;
  const cards = content?.contact_us_tiles?.card_group_tiles?.[0]?.card || [];

  return {
    aboutFormDesc: aboutFormMessage,
    requiredMessage: contactUsDetails.required_message,
    formTitle: content?.page_name?.title,
    subjects: subjectData,
    successMessage: {
      heading: content.messages.after_success.heading,
      description: content.messages.after_success.body,
      showIcon: content.messages.show_icon,
    },
    errorMessage: {
      heading: content.messages.error_message.heading,
      description: content.messages.error_message.body,
      showIcon: content.messages.error_message.show_icon,
    },
    contact_us_tiles: content?.contact_us_tiles?.contact_us_tile,
    contact_us_card_tiles: content?.contact_us_tiles?.card_group_tiles,
    card_navigation: unwrapNavigation(cards, SITE_MAP),
    defaultStoreLocation: contactUsDetails.default_list_of_stores?.store_name,
  };
}
export function unwrapNavigation(cards: any, SITE_MAP: ISiteMap) {
  if (!cards) return [];
  return cards.map((card: any) => {
    return unwrapCard(card, SITE_MAP);
  });
}

export function unwrapIngredientsList(content: any): Array<IIngredientsList> {
  const ingredientList = safeGet(content, 'ingredient_list', []);
  return ingredientList.map((ingredient: any) => {
    return {
      label: safeGet(ingredient, 'ingredient_name', ''),
      title: safeGet(ingredient, 'title', ''),
    };
  });
}

export function unwrapMedicineSKUImages(content: any): IMedicineImages {
  const imagesList = safeGet(content, 'variant', []);
  const finalImageList: IMedicineImages = {};
  imagesList.map((image: any) => {
    const imagesArr: IImages[] = [];
    image.images.map((record: any) => {
      imagesArr.push({
        imageSrc: safeGet(record, 'url', ''),
        imageAlt: safeGet(record, 'title', ''),
      });
    });
    finalImageList[image.sku] = imagesArr;
  });
  return finalImageList;
}

export function unwrapDefaultMedicineImages(content: any): Array<IMedicineImages> {
  const imagesList = safeGet(content, 'images', []);
  return imagesList.map((image: any) => {
    return {
      imageSrc: safeGet(image, 'url', ''),
      imageAlt: safeGet(image, 'title', ''),
    };
  });
}

export function unwrapCheckout(content: any, SITE_MAP: ISiteMap): { url: string } {
  return {
    url: safeGet(content, 'global_field.page_name'),
  };
}
export interface IRegionPicker {
  fullName: string;
  shortName: string;
  isExternal: boolean;
  href: string;
  regionCode: string;
}

export function unwrapRegionPicker(content: any) {
  if (!content) return [];
  const kp_regions = safeGet(content[0][0], 'kp_regions', []);
  if (!kp_regions) return [];
  const regions: IRegionPicker[] = [];
  for (let item = 0; item < kp_regions.length; item++) {
    const regionData: IRegionPicker = {
      fullName: '',
      shortName: '',
      isExternal: false,
      href: '',
      regionCode: '',
    };

    regionData.isExternal = kp_regions[item].is_released ? false : true;
    regionData.fullName = kp_regions[item].display_name;
    regionData.shortName = kp_regions[item].display_name;
    regionData.href = kp_regions[item].legacy_url.href;
    regionData.regionCode = kp_regions[item].region_code;

    regions.push(regionData);
  }
  return regions;
}

export const unwrapFAQVirtualAssistant = (content: any, SITE_MAP: ISiteMap) => {
  if (content && content?.faq_va[0]) {
    return {
      faqContent: content?.faq_va[0]?.faqs?.map((item: any) => item?.faq),
      faqPageLink: content?.faq_va[0]?.faq_page_link?.faqs_link?.page_name,
      faqVAHeader: content?.faq_va[0]?.virtual_assistant_heading,
    };
  } else {
    return {
      faqContent: undefined,
      faqPageLink: '/faqs',
      faqVAHeader: 'How can we help you today?',
    };
  }
};
